import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import type { Case } from '../../domain/initial-cases';
import { getPrefs } from '../../lib/prefs';

export interface UIStateValue {
  page: string;
  setPage: Dispatch<SetStateAction<string>>;
  selectedCase: Case | null;
  setSelectedCase: Dispatch<SetStateAction<Case | null>>;
  caseTab: string;
  setCaseTab: Dispatch<SetStateAction<string>>;
  modal: string | null;
  setModal: Dispatch<SetStateAction<string | null>>;
  // modalData carries per-modal payload shapes (validation: { missing, message };
  // shaft-response: { caseId, disputeId, responseType }; etc). The original
  // useState was `any`; consumers access nested properties freely. Keeping
  // `any` avoids fanning out per-modal type discriminants in this PR.
  modalData: any;
  setModalData: Dispatch<SetStateAction<any>>;
  auditFilter: string;
  setAuditFilter: Dispatch<SetStateAction<string>>;
  kycExpanded: string | null;
  setKycExpanded: Dispatch<SetStateAction<string | null>>;
  caseSearch: string;
  setCaseSearch: Dispatch<SetStateAction<string>>;
  navContext: string | null;
  setNavContext: Dispatch<SetStateAction<string | null>>;
  openCase: (c: Case, context?: string) => void;
  /**
   * #539 — the intake submission the Intake page should open when it next
   * renders, or null. Written by a cross-page link (the Registrations tab of a
   * customer); read, acted on and cleared by pages/Intake.tsx.
   *
   * It is a plain id and not the row, unlike `selectedCase`. The linking page
   * has a submission in hand, but the one it holds came from its own 200-row
   * query and can be stale by the time the operator clicks; Intake refetches
   * the id so what the reviewer reads is current. It also means a caller needs
   * nothing but the id to link.
   */
  focusedSubmissionId: string | null;
  setFocusedSubmissionId: Dispatch<SetStateAction<string | null>>;
  openSubmission: (submissionId: string) => void;
}

const UIStateContext = createContext<UIStateValue | null>(null);

export function UIStateProvider({ children }: { children: ReactNode }) {
  // #399 — the default landing page. Seeded, not applied by an effect: an
  // effect would render the dashboard first and then swap, which is a visible
  // flash and one wasted data fetch on every sign-in. LANDING_PAGES excludes
  // the role-gated pages, so this cannot strand a non-admin on a page they
  // may not open.
  const [page, setPage] = useState<string>(() => getPrefs().landingPage);
  const [selectedCase, setSelectedCase] = useState<Case | null>(null);
  const [caseTab, setCaseTab] = useState('overview');
  const [modal, setModal] = useState<string | null>(null);
  const [modalData, setModalData] = useState<any>({});
  const [auditFilter, setAuditFilter] = useState('');
  const [kycExpanded, setKycExpanded] = useState<string | null>(null);
  const [caseSearch, setCaseSearch] = useState('');
  const [navContext, setNavContext] = useState<string | null>(null);
  const [focusedSubmissionId, setFocusedSubmissionId] = useState<string | null>(null);

  const openCase = useCallback((c: Case, context?: string) => {
    setSelectedCase(c);
    setCaseTab('overview');
    setPage('case-detail');
    if (context) setNavContext(context);
  }, []);

  // #539 — the openCase of the Intake queue: name the target, then switch pages.
  // Intake owns everything about how a submission is displayed, so this sets a
  // handle rather than reaching into that page's state, exactly as openCase
  // leaves the case tabs to CaseDetail.
  const openSubmission = useCallback((submissionId: string) => {
    setFocusedSubmissionId(submissionId);
    setPage('intake');
  }, []);

  const value: UIStateValue = useMemo(() => ({
    page, setPage,
    selectedCase, setSelectedCase,
    caseTab, setCaseTab,
    modal, setModal,
    modalData, setModalData,
    auditFilter, setAuditFilter,
    kycExpanded, setKycExpanded,
    caseSearch, setCaseSearch,
    navContext, setNavContext,
    openCase,
    focusedSubmissionId, setFocusedSubmissionId,
    openSubmission,
  }), [page, selectedCase, caseTab, modal, modalData, auditFilter, kycExpanded, caseSearch, navContext, openCase, focusedSubmissionId, openSubmission]);

  return <UIStateContext.Provider value={value}>{children}</UIStateContext.Provider>;
}

export function useUIState(): UIStateValue {
  const ctx = useContext(UIStateContext);
  if (!ctx) throw new Error('useUIState must be used within a UIStateProvider');
  return ctx;
}
